import React from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { WindowLocationService } from '~src/commonInfra/services/WindowLocationService';

import { DataSources } from '../../../datasource/resources/Datasources';
import { MobileAppBanner } from '../../../mobile/components/MobileAppBanner';
import { StartPageService } from '../../../settings/services/StartPageService';
import { shouldResetPreviousUrl } from '../../../ts/helpers/UrlHelpers';
import { SiteSwitchService } from '../../../ts/siteManagement/services/SiteSwitchService';
import { postLoginNotification } from '../../../zeus/PostLoginNotification';
import { siteExpirationNotification } from '../../../zeus/SiteExpirationNotification';
import { updateExplorerDisplayMode } from '../../actions/DisplaySettingsActions';
import { incrementRouterSpaceReactKey } from '../../actions/spaces/IncrementRouterSpaceReactKeyAction';
import { requestSpaceContents } from '../../actions/spaces/RequestSpaceContentsActions';
import { setPreviousUrl } from '../../actions/spaces/SetPreviousUrlReducerAction';
import { setDisplayModeFromUrlIfParamIsPresent } from '../../content/DisplaySettingsUrlBindings';
import { ConnectedLeftNav } from '../../layout/ConnectedLeftNav';
import { ConnectedPageHeader } from '../../layout/ConnectedPageHeader';
import { ConnectedSkipBarProvider } from '../../layout/SkipBarProvider';
import { siteUrlsSelector } from '../../selectors/SiteUrlsSelectors';
import { selectSpaceInfo } from '../../selectors/SpaceInfoSelectors';
import { AppState } from '../../state/AppState';
import { isMetricSpace } from '../../state/SpaceInfo';
import { MapDispatchToProps, MapStateToProps } from '../../utils/ConnectUtils';
import { CustomDragLayer } from '../CustomDragLayer';
import { ServerSpaceRouter } from '../ServerSpaces/ServerSpaceRouter';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { BasePathsUtils } from './BasePathsUtils';
import { SiteSpaceRouter } from './SiteSpaceRouter';

export interface MainOwnProps {
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
  readonly fromUnitTests?: boolean; // Hooks don't work well with shallow tests so dirty workaround
}

export interface MainStateProps {
  readonly urls: SpacesSiteUrls;
  readonly isMetricSpace: boolean;
}

export interface MainDispatchProps {
  readonly setPreviousUrl: (url: string) => void;
  readonly incrementRouterSpaceReactKey: () => void;
  readonly requestSpaceContents: () => void;
  readonly updateExplorerDisplayMode: typeof updateExplorerDisplayMode;
}

export type MainProps = MainOwnProps & MainStateProps & MainDispatchProps;

export const MainComponent: React.FunctionComponent<MainProps> = props => {

  const siteNameFromCurrentPath = parseOutSiteUrlName(props.routeLocationPath);
  const siteNameFromRedux = parseOutSiteUrlName(props.urls.homePath());
  const [path, setPath] = React.useState(siteNameFromCurrentPath === siteNameFromRedux ? props.routeLocationPath : '');

  React.useEffect(
    () => {
      const updateExplorerDisplayMode = props.updateExplorerDisplayMode;
      const queryString: any = WindowLocationService.getHashQueryObject();
      setDisplayModeFromUrlIfParamIsPresent(queryString, updateExplorerDisplayMode);
    },
    [ path, props.routeLocationSearch, props.updateExplorerDisplayMode]
  );

  React.useEffect(
    () => {
      if (postLoginNotification) {
        postLoginNotification.showNotificationsIfEnabled();
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ postLoginNotification ]
  );

  React.useEffect(
    () => {
      if (!props.fromUnitTests && siteExpirationNotification) {
        siteExpirationNotification.showNotificationsIfEnabled();
      }
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ props.fromUnitTests, siteExpirationNotification ]
  );

  React.useEffect(() => {
    let ignore = false;
    if (props.routeLocationPath !== path) {
      const siteNameFromCurrentPath = parseOutSiteUrlName(props.routeLocationPath);
      const isChangingSites = parseOutSiteUrlName(path) !== siteNameFromCurrentPath;
      if (isChangingSites && !_.isUndefined(siteNameFromCurrentPath)) {
        SiteSwitchService.switchIfNeeded(siteNameFromCurrentPath)
          .finally(() => {
            if (!ignore) {
              setPath(props.routeLocationPath);
            }
          });
      } else {
        if (!ignore) {
          setPath(props.routeLocationPath);
        }
      }

      // Includes resetting when navigating to /server which isChangingSites will catch
      if (shouldResetPreviousUrl(props.routeLocationPath, isChangingSites)){
        props.setPreviousUrl('');
      }
    }

    return () => ignore = true;
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.routeLocationPath]);

  if (props.fromUnitTests) { return null; }

  const constrainedClass = props.isMetricSpace ? '' : 'tb-main-constrained';
  const defaultRedirectPath = getDefaultRedirectPath(props.routeLocationPath);
  return (
    <div className={'tb-column-flex-parent tb-fill'}>
      <ConnectedSkipBarProvider />
      <MobileAppBanner />
      <div className={'tb-flex-fill ' + constrainedClass}>
        <div className={'tb-row-flex-parent tb-full-height'}>
          <ConnectedLeftNav />
          <div className={'tb-flex-fill tb-column-flex-parent tb-main-content'}>
            <ConnectedPageHeader datasourcesResource={DataSources}/>
            <div className={'tb-flex-fill'}>
                <CustomDragLayer/>
                <Switch>
                  <Route exact from={props.urls.defaultPath()}>
                    <Redirect exact from={props.urls.defaultPath()} to={defaultRedirectPath}/>
                  </Route>
                  <Route exact from={'/'}>
                    <Redirect exact from={props.urls.defaultPath()} to={defaultRedirectPath}/>
                  </Route>
                  <Route path={'/server'}>
                    <ServerSpaceRouter
                      routeLocationPath={props.routeLocationPath}
                      routeLocationSearch={props.routeLocationSearch}
                    />
                  </Route>
                  <Route>
                    <SiteSpaceRouter
                      isBusy={parseOutSiteUrlName(path) !== siteNameFromCurrentPath}
                      siteName={siteNameFromCurrentPath}
                      routeLocationPath={path} // Don't want to render until the site switch is done and path has been updated
                      routeLocationSearch={props.routeLocationSearch}
                    />
                  </Route>
                </Switch>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const parseOutSiteUrlName = (path: string): string  => {
  if (path.startsWith('/site/')) {
    path = path.substring(6); // chop off the initial '/site/'
    const endSiteNamePattern = new RegExp('[/?]');
    const endSiteNameIndex = path.search(endSiteNamePattern);
    if (endSiteNameIndex === -1) {
      return path === '' ? undefined : path;
    }
    const siteUrlName = path.substring(0, endSiteNameIndex);
    return siteUrlName === '' ? undefined : siteUrlName;
  }

  // if its a valid site space path return empty string to indicate default
  const urls = new SpacesSiteUrls({ siteUrlName: '' });
  const basePaths = BasePathsUtils.getWatchedBasePathsForSite(urls);
  if (_.some(basePaths, (basePath) => path.startsWith(basePath))) {
    return '';
  }

  // these should be /server/ paths or invalid paths
  return undefined;
};

const getDefaultRedirectPath = (routeLocationPath: string): string => {
  const siteUrlName = parseOutSiteUrlName(routeLocationPath);
  if (!_.isUndefined(siteUrlName) && siteUrlName.length > 0) {
    return StartPageService.getCurrentUserStartPageForSite(siteUrlName);
  } else {
    return StartPageService.getCurrentUserStartPageForCurrentSite();
  }
};

const mapStateToProps: MapStateToProps<MainStateProps> = (state: AppState) => ({
  urls: siteUrlsSelector(state),
  isMetricSpace: isMetricSpace(selectSpaceInfo(state))
});

const mapDispatchToProps: MapDispatchToProps<MainDispatchProps> = (dispatch) => bindActionCreators({
  incrementRouterSpaceReactKey,
  setPreviousUrl,
  requestSpaceContents,
  updateExplorerDisplayMode
}, dispatch);

export const Main: React.ComponentClass<MainOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(MainComponent);

